import { AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useAzureDevOpsIngestionStatus } from '@/hooks/useAzureDevOpsIngestion'
import { describeIngestionChainProblem } from '@/lib/ingestionChainProblem'
import { Permissions } from '@/lib/permissions'

interface AzureDevOpsIngestionWarningProps {
  projectId: string | undefined
}

interface IngestionAlertProps {
  testId: string
  message: string
  settingsPath: string | null
}

function IngestionAlert({ testId, message, settingsPath }: IngestionAlertProps) {
  return (
    <p
      role="alert"
      className="flex items-start gap-2 rounded border border-danger-foreground bg-danger px-4 py-3 text-sm text-danger-foreground"
      data-testid={testId}
    >
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
      <span>
        {message}
        {settingsPath && (
          <>
            {' '}
            <Link to={settingsPath} className="underline hover:text-foreground">
              Open project settings
            </Link>
          </>
        )}
      </span>
    </p>
  )
}

export function AzureDevOpsIngestionWarning({ projectId }: AzureDevOpsIngestionWarningProps) {
  const { data: status } = useAzureDevOpsIngestionStatus(projectId)
  const { hasPermission, isAdmin } = useAuth(projectId)

  if (!projectId || !status?.isEnabled) return null

  const chainProblem = describeIngestionChainProblem(status)
  if (!status.lastError && !chainProblem) return null

  const canManageSettings = isAdmin || hasPermission(Permissions.Project.Settings.Manage, projectId)
  const settingsPath = canManageSettings ? `/projects/${projectId}/settings?tab=azure-devops-ingestion` : null

  return (
    <>
      {status.lastError && (
        <IngestionAlert
          testId="backlog-ado-ingestion-warning"
          message={`Automatic Azure DevOps ingestion is failing, so new work items are not reaching this backlog: ${status.lastError}`}
          settingsPath={settingsPath}
        />
      )}
      {chainProblem && (
        <IngestionAlert testId="backlog-ado-ingestion-chain-warning" message={chainProblem} settingsPath={settingsPath} />
      )}
    </>
  )
}
